export function shouldPlaySelectedVideo(state) {
  return Boolean(
    state.selected
      && state.sectionVisible
      && state.documentVisible,
  );
}

export function getPracticePanelState(id, selectedId, mobile) {
  const selected = id === selectedId;

  return {
    selected,
    panelOpen: Boolean(mobile && selected),
  };
}

function isMobileMedia() {
  return window.matchMedia('(max-width: 749px)').matches;
}

function syncVideoPoster(video) {
  if (!video) return;

  const host = video.closest('[data-responsive-video-poster]');
  if (!host) return;

  const poster = isMobileMedia()
    ? host.dataset.mobilePoster || host.dataset.poster || ''
    : host.dataset.poster || '';

  if (poster && video.getAttribute('poster') !== poster) {
    video.setAttribute('poster', poster);
  }
}

const elementName = 'aceii-practice-shift';

if (
  typeof window !== 'undefined'
  && typeof HTMLElement !== 'undefined'
  && !window.customElements.get(elementName)
) {
  class AceiiPracticeShift extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.buttons = [...this.querySelectorAll('[data-practice-select]')];
      this.media = [...this.querySelectorAll('[data-practice-media]')];
      this.visual = this.querySelector('[data-practice-visual]');
      this.visualHome = this.querySelector('[data-practice-visual-home]');
      this.mobilePanels = [
        ...this.querySelectorAll('[data-practice-mobile-panel]'),
      ];
      this.mobilePanelById = new Map(
        this.mobilePanels.map((panel) => [
          panel.dataset.practiceMobilePanel,
          panel,
        ]),
      );
      this.mobileMedia = window.matchMedia('(max-width: 760px)');
      this.sectionVisible = false;
      this.documentVisible = !document.hidden;
      this.currentId =
        this.querySelector('[data-practice-select].is-active')?.dataset
          .practiceSelect
        ?? this.buttons[0]?.dataset.practiceSelect
        ?? '';

      const { signal } = this.abortController;

      this.buttons.forEach((button) => {
        button.addEventListener(
          'click',
          () => this.select(button.dataset.practiceSelect),
          { signal },
        );
        button.addEventListener(
          'mouseenter',
          () => {
            if (window.matchMedia('(min-width: 900px)').matches) {
              this.select(button.dataset.practiceSelect);
            }
          },
          { signal },
        );
      });

      this.querySelectorAll('video').forEach(syncVideoPoster);

      this.mobileMedia.addEventListener(
        'change',
        () => {
          this.querySelectorAll('video').forEach(syncVideoPoster);
          this.syncResponsiveLayout();
        },
        { signal },
      );

      document.addEventListener(
        'visibilitychange',
        () => {
          this.documentVisible = !document.hidden;
          if (this.documentVisible) {
            this.resumeSelectedVideo();
          } else {
            this.pauseSelectedVideoAutomatically();
          }
        },
        { signal },
      );

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.sectionVisible = Boolean(entry?.isIntersecting);
          if (this.sectionVisible) {
            this.resumeSelectedVideo();
          } else {
            this.pauseSelectedVideoAutomatically();
          }
        },
        {
          rootMargin: '200px 0px',
          threshold: 0.01,
        },
      );
      this.observer.observe(this);

      this.select(this.currentId);
    }

    disconnectedCallback() {
      this.restoreVisualHome();
      this.pauseAllVideos();
      this.observer?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
    }

    get selectedMedia() {
      return this.media.find(
        (item) => item.dataset.practiceMedia === this.currentId,
      );
    }

    get selectedVideo() {
      return this.selectedMedia?.querySelector('video') ?? null;
    }

    select(id) {
      if (!id) return;

      this.currentId = id;

      this.buttons.forEach((button) => {
        const state = getPracticePanelState(
          button.dataset.practiceSelect,
          id,
          this.mobileMedia.matches,
        );
        button.classList.toggle('is-active', state.selected);
        button.setAttribute('aria-expanded', String(state.selected));
      });

      this.media.forEach((item) => {
        const active = item.dataset.practiceMedia === id;
        item.classList.toggle('is-active', active);
        item.hidden = !active;
        item.setAttribute('aria-hidden', String(!active));

        if (!active) {
          this.pauseVideo(item.querySelector('video'));
        }
      });

      this.syncResponsiveLayout();
      this.syncSelectedVideo();
    }

    restoreVisualHome() {
      if (this.visual && this.visualHome) {
        this.visualHome.after(this.visual);
      }
    }

    syncResponsiveLayout() {
      const mobile = this.mobileMedia.matches;
      let openPanel = null;

      this.buttons.forEach((button) => {
        const state = getPracticePanelState(
          button.dataset.practiceSelect,
          this.currentId,
          mobile,
        );
        const panel = this.mobilePanelById.get(button.dataset.practiceSelect);

        button.setAttribute(
          'aria-controls',
          mobile
            ? button.dataset.practicePanelControls
            : button.dataset.practiceMediaControls,
        );

        if (!panel) return;

        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
        panel.inert = true;

        if (state.panelOpen) openPanel = panel;
      });

      if (!mobile || !openPanel) {
        this.restoreVisualHome();
        return;
      }

      const panelInner = openPanel.querySelector(
        '[data-practice-mobile-panel-inner]',
      );
      panelInner?.append(this.visual);
      openPanel.inert = false;
      openPanel.setAttribute('aria-hidden', 'false');
      openPanel.classList.add('is-open');
    }

    ensureVideoSource(video) {
      if (!video || video.currentSrc || video.src) return;

      syncVideoPoster(video);

      const pendingSources = [
        ...video.querySelectorAll('source[data-src]'),
      ];
      if (pendingSources.length) {
        pendingSources.forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        video.load();
        return;
      }

      if (!video.dataset.src) return;

      video.src = video.dataset.src;
      video.load();
    }

    pauseVideo(video) {
      if (!video) return;

      if (!video.paused) video.pause();
    }

    pauseAllVideos() {
      this.querySelectorAll('video').forEach((video) => {
        this.pauseVideo(video);
      });
    }

    pauseSelectedVideoAutomatically() {
      this.pauseVideo(this.selectedVideo);
    }

    async playSelectedVideo() {
      const video = this.selectedVideo;
      if (!video) return;

      if (
        !shouldPlaySelectedVideo({
          selected: true,
          sectionVisible: this.sectionVisible,
          documentVisible: this.documentVisible,
        })
      ) {
        return;
      }

      this.ensureVideoSource(video);
      video.muted = true;
      video.loop = true;
      video.playsInline = true;

      try {
        await video.play();

        if (video !== this.selectedVideo) {
          this.pauseVideo(video);
          return;
        }

        this.removeAttribute('data-playback-blocked');
      } catch {
        this.setAttribute('data-playback-blocked', 'true');
      }

    }

    resumeSelectedVideo() {
      this.playSelectedVideo();
    }

    syncSelectedVideo() {
      this.querySelectorAll('video').forEach((video) => {
        if (video !== this.selectedVideo) {
          this.pauseVideo(video);
        }
      });

      this.playSelectedVideo();
    }
  }

  window.customElements.define(elementName, AceiiPracticeShift);
}
